import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

const SUPABASE_URL = 'https://yergxyxcsqvismxjeswk.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inllcmd4eXhjc3F2aXNteGplc3drIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTc1NTU1MDUsImV4cCI6MjA3MzEzMTUwNX0.TTtWLnhYuAIwiRlVolQ7R-TXYqjkmKI7auR5j2Egs7o';

interface UserProfile {
  id: string;
  full_name: string;
  phone_number: string;
  email: string;
  role: 'customer' | 'technician';
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  initialized: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [profileFetching, setProfileFetching] = useState(false);

  // Fallback timeout to ensure app never gets stuck loading
  React.useEffect(() => {
    const fallbackTimeout = setTimeout(() => {
      if (loading) {
        setLoading(false);
        setInitialized(true);
      }
    }, 10000); // 10 second fallback

    return () => clearTimeout(fallbackTimeout);
  }, [loading]);



  const fetchProfile = React.useCallback(async (userId: string, userEmail?: string) => {
    // Prevent multiple simultaneous profile fetches
    if (profileFetching) {
      return null;
    }

    try {
      setProfileFetching(true);
      
      // Try Supabase client first with timeout
      const profilePromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile fetch timeout')), 3000)
      );
      
      const { data, error } = await Promise.race([profilePromise, timeoutPromise]) as any;

      // If Supabase client fails with "Failed to fetch", try direct REST API
      if (error && error.message.includes('Failed to fetch')) {
        const { data: { session } } = await supabase.auth.getSession();
        
        try {
          const directResponse = await fetch(`${SUPABASE_URL}/rest/v1/profiles?select=*&id=eq.${userId}`, {
            method: 'GET',
            headers: {
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${session?.access_token || SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (directResponse.ok) {
            const directData = await directResponse.json();
            
            if (directData && directData.length > 0) {
              return directData[0];
            } else {
              // Profile doesn't exist, create one
              const basicProfile = {
                id: userId,
                full_name: userEmail?.split('@')[0] || 'User',
                phone_number: '',
                email: userEmail || '',
                role: 'customer' as const,
                created_at: new Date().toISOString()
              };
              
              // Create profile via direct REST API
              const createResponse = await fetch(`${SUPABASE_URL}/rest/v1/profiles`, {
                method: 'POST',
                headers: {
                  'apikey': SUPABASE_ANON_KEY,
                  'Authorization': `Bearer ${session?.access_token || SUPABASE_ANON_KEY}`,
                  'Content-Type': 'application/json',
                  'Prefer': 'return=representation'
                },
                body: JSON.stringify(basicProfile)
              });
              
              if (createResponse.ok) {
                const createdData = await createResponse.json();
                return createdData[0] || basicProfile;
              } else {
                return basicProfile;
              }
            }
          }
        } catch (directErr) {
          // Fall through to error handling
        }
      }

      if (error) {
        // If profile doesn't exist, create a basic one
        if (error.code === 'PGRST116' || error.message?.includes('No rows found')) {
          const basicProfile = {
            id: userId,
            full_name: userEmail?.split('@')[0] || 'User',
            phone_number: '',
            email: userEmail || '',
            role: 'customer' as const,
            created_at: new Date().toISOString()
          };

          // Try to insert the basic profile
          try {
            await supabase.from('profiles').insert([basicProfile]);
          } catch (insertErr) {
            // Ignore insert errors
          }

          return basicProfile;
        }
        return null;
      }

      return data;
    } catch (error) {
      // Return a basic profile if fetch fails (network issues, etc.)
      const fallbackProfile = {
        id: userId,
        full_name: userEmail?.split('@')[0] || 'User',
        phone_number: '',
        email: userEmail || '',
        role: 'customer' as const,
        created_at: new Date().toISOString()
      };
      return fallbackProfile;
    } finally {
      setProfileFetching(false);
    }
  }, [profileFetching]);

  const refreshProfile = async () => {
    if (user) {
      const profileData = await fetchProfile(user.id, user.email);
      setProfile(profileData);
    }
  };

  const signIn = async (email: string, password: string) => {
    // Check if user is already signed in
    if (user && user.email === email) {
      if (!profile) {
        try {
          const profileData = await fetchProfile(user.id, user.email);
          setProfile(profileData);
        } catch (error) {
          // Set a basic profile if fetch fails
          setProfile({
            id: user.id,
            full_name: 'User',
            phone_number: '',
            email: user.email || '',
            role: 'customer',
            created_at: new Date().toISOString()
          });
        }
      }
      return;
    }
    
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (data.user) {
        setUser(data.user);
        const profileData = await fetchProfile(data.user.id, data.user.email);
        setProfile(profileData);
      } else {
        throw new Error('No user returned from authentication');
      }
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw new Error(error.message);
      }
      setUser(null);
      setProfile(null);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    
    // Get initial session with timeout
    const getInitialSession = async () => {
      try {
        // Add timeout to prevent hanging
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session timeout')), 5000)
        );
        
        const { data: { session }, error } = await Promise.race([sessionPromise, timeoutPromise]) as any;
        
        if (!mounted) return;
        
        if (error) {
          // Handle error silently
          setLoading(false);
          setInitialized(true);
        } else if (session?.user) {
          setUser(session.user);
          setLoading(false);
          setInitialized(true);
          // Fetch profile in background without causing re-renders
          fetchProfile(session.user.id, session.user.email).then(profileData => {
            if (mounted) {
              setProfile(profileData);
            }
          });
        } else {
          setLoading(false);
          setInitialized(true);
        }
      } catch (error) {
        if (mounted) {
          setLoading(false);
          setInitialized(true);
        }
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;
        
        // Only process auth changes after initial load
        if (!initialized) {
          return;
        }
        
        if (session?.user) {
          setUser(session.user);
          // Only fetch profile if we don't have one or user changed
          if (!profile || profile.id !== session.user.id) {
            fetchProfile(session.user.id, session.user.email).then(profileData => {
              if (mounted) {
                setProfile(profileData);
              }
            });
          }
        } else {
          setUser(null);
          setProfile(null);
        }
        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    user,
    profile,
    loading,
    initialized,
    signIn,
    signOut,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
